<script setup lang="ts">
  import { computed, type HTMLAttributes } from "vue";
  import { TagsInputRoot, TagsInputInput, TagsInputItem, TagsInputItemText, TagsInputItemDelete } from "reka-ui";
  import { X } from "lucide-vue-next";
  import { cn } from "@/lib/utils";

  const props = withDefaults(
    defineProps<{
      modelValue?: string[];
      placeholder?: string;
      class?: HTMLAttributes["class"];
    }>(),
    {
      modelValue: () => [],
      placeholder: "Type and press Enter",
      class: undefined,
    },
  );

  const emit = defineEmits<{
    "update:modelValue": [value: string[]];
  }>();

  const values = computed(() => props.modelValue ?? []);

  const handleUpdate = (next: string[]) => {
    // De-dupe + strip empty values so the parent never holds garbage.
    const cleaned = Array.from(new Set(next.map((v) => v.trim()).filter((v) => v.length > 0)));
    emit("update:modelValue", cleaned);
  };
</script>

<template>
  <TagsInputRoot
    :model-value="values"
    :add-on-paste="true"
    :add-on-blur="true"
    :add-on-tab="true"
    :class="cn(
      'flex min-h-9 w-full flex-wrap items-center gap-1 rounded-md border border-input bg-transparent px-2 py-1 text-sm shadow-xs focus-within:ring-1 focus-within:ring-ring',
      props.class,
    )"
    @update:model-value="handleUpdate">
    <TagsInputItem
      v-for="item in values"
      :key="item"
      :value="item"
      class="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-0.5 text-xs text-secondary-foreground">
      <TagsInputItemText>{{ item }}</TagsInputItemText>
      <TagsInputItemDelete class="rounded-full outline-none hover:bg-muted-foreground/20 focus:bg-muted-foreground/20">
        <X class="size-3" />
      </TagsInputItemDelete>
    </TagsInputItem>
    <TagsInputInput
      :placeholder="values.length ? '' : placeholder"
      class="flex-1 min-w-[80px] bg-transparent py-0.5 outline-none placeholder:text-muted-foreground" />
  </TagsInputRoot>
</template>
